import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users, RefreshCw, Circle, Phone, Coffee, WifiOff, PhoneOff,
  Clock, ChevronRight, Calendar, DollarSign,
} from "lucide-react";
import { toast } from "sonner";

// Rep Activity — real-time monitoring + payroll-grade calling-time report.
// Backed by rep_activity_service.py. Refreshes live status every 15s.
// Click any rep to open a drawer with the day/week timeline + payroll totals.

const RANGES = [
  { key: "today",     label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "last_week", label: "Last Week" },
];

const STATUS_META = {
  dialing:           { color: "bg-emerald-500", ring: "ring-emerald-200", label: "Dialing", icon: Phone,  tone: "text-emerald-700" },
  on_call:           { color: "bg-amber-500",   ring: "ring-amber-200",   label: "On Call", icon: Phone,  tone: "text-amber-700" },
  idle:              { color: "bg-red-500",     ring: "ring-red-200",     label: "IDLE",    icon: PhoneOff, tone: "text-red-700" },
  break:             { color: "bg-sky-500",     ring: "ring-sky-200",     label: "Break",   icon: Coffee, tone: "text-sky-700" },
  clocked_in_no_dial:{ color: "bg-slate-400",   ring: "ring-slate-200",   label: "Standby", icon: Clock,  tone: "text-slate-600" },
  offline:           { color: "bg-slate-300",   ring: "ring-slate-100",   label: "Offline", icon: WifiOff, tone: "text-slate-500" },
};

function fmt(sec) {
  if (!sec || sec < 0) return "0s";
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
}

export default function RepActivity() {
  const [rows, setRows]     = useState([]);
  const [busy, setBusy]     = useState(false);
  const [openRep, setRep]   = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/rep-activity/live");
      setRows(data?.rows || []);
    } catch { setRows([]); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15_000);
    // Heartbeat — lets the backend detect session-disconnect for the
    // currently-logged-in user (this admin is watching the dashboard).
    const hb = setInterval(() => { api.post("/rep-activity/heartbeat").catch(() => {}); }, 30_000);
    return () => { clearInterval(t); clearInterval(hb); };
  }, [load]);

  // Open the deep-linked rep report if ?rep=<id> is set (from Telegram link)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("rep");
    if (p) setRep({ rep_id: p });
  }, []);

  const summary = useMemo(() => {
    const c = { dialing: 0, on_call: 0, idle: 0, break: 0, offline: 0, clocked_in_no_dial: 0 };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="p-6 space-y-4" data-testid="rep-activity-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Users className="text-purple-500" size={24} /> Rep Activity
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            Real-time rep status backed by actual dialer events. Click any rep for the timeline + payroll calling time.
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={busy}
                data-testid="rep-activity-refresh" className="rounded-full">
          <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-6 gap-3">
        {["dialing", "on_call", "idle", "break", "clocked_in_no_dial", "offline"].map((k) => {
          const meta = STATUS_META[k];
          const Icon = meta.icon;
          return (
            <div key={k} className="rounded-xl border-2 border-slate-200 bg-white p-3 shadow-sm">
              <div className={`flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-widest ${meta.tone}`}>
                <Icon size={11} /> {meta.label}
              </div>
              <div className="text-2xl font-bold mt-0.5">{summary[k] || 0}</div>
            </div>
          );
        })}
      </div>

      {/* Live rows */}
      <div className="rounded-2xl border-2 border-slate-200 overflow-hidden bg-white shadow-sm">
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50 px-4 py-2.5 border-b">
          <div className="col-span-4">Rep</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-4">Detail</div>
          <div className="col-span-2 text-right">Calling Today</div>
        </div>
        {rows.length === 0 && !busy && (
          <div className="px-4 py-16 text-center text-sm text-muted-foreground">
            No reps to monitor yet. Invite your team from Settings → Team.
          </div>
        )}
        {rows.map((r) => {
          const meta = STATUS_META[r.status] || STATUS_META.offline;
          return (
            <div key={r.rep_id}
                 onClick={() => setRep(r)}
                 className="grid grid-cols-12 items-center px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 cursor-pointer transition"
                 data-testid={`rep-row-${r.rep_id}`}>
              <div className="col-span-4">
                <div className="font-semibold text-sm truncate">{r.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {(r.role || "").replace(/_/g, " ")}
                </div>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <span className={`inline-flex h-2 w-2 rounded-full ${meta.color} ring-4 ${meta.ring}`} />
                <span className={`text-xs font-semibold ${meta.tone}`}>{meta.label}</span>
              </div>
              <div className="col-span-4 text-xs text-slate-700 truncate" title={r.detail}>
                {r.detail}
              </div>
              <div className="col-span-2 flex items-center justify-end gap-1 text-xs font-semibold text-slate-800">
                {fmt(r.today_calling_sec)} <ChevronRight size={13} className="text-slate-400" />
              </div>
            </div>
          );
        })}
      </div>

      <RepReportDrawer rep={openRep} onClose={() => setRep(null)} />
    </div>
  );
}


// ============================ REPORT DRAWER ============================
function RepReportDrawer({ rep, onClose }) {
  const [rangeKey, setRangeKey] = useState("today");
  const [data, setData]         = useState(null);
  const [busy, setBusy]         = useState(false);

  useEffect(() => {
    if (!rep?.rep_id) return;
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/rep-activity/report/${rep.rep_id}`, {
          params: { range_key: rangeKey },
        });
        setData(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Report failed");
      } finally { setBusy(false); }
    })();
  }, [rep, rangeKey]);

  if (!rep) return null;
  const isDay  = !!data?.timeline;
  const isWeek = !!data?.days;

  return (
    <Dialog open={!!rep} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rep-activity-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="text-purple-500" size={20} />
            {rep.name || "Rep"} · Activity
          </DialogTitle>
        </DialogHeader>

        {/* Range picker */}
        <div className="flex flex-wrap gap-2 border-b pb-3">
          {RANGES.map((r) => (
            <button key={r.key}
                    onClick={() => setRangeKey(r.key)}
                    data-testid={`rep-range-${r.key}`}
                    className={`px-3 py-1 rounded-full text-xs font-medium border-2 transition ${
                      rangeKey === r.key
                        ? "bg-slate-900 border-slate-900 text-white"
                        : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
                    }`}>
              {r.label}
            </button>
          ))}
        </div>

        {busy && <div className="py-10 text-center text-sm text-muted-foreground">Loading…</div>}

        {/* DAY VIEW — timeline + totals */}
        {isDay && !busy && (
          <div className="space-y-4">
            <div className="rounded-lg border overflow-hidden">
              <div className="grid grid-cols-4 text-[10px] uppercase font-semibold text-slate-500 bg-slate-50 px-3 py-2">
                <div>Start</div><div>End</div><div>Activity</div><div className="text-right">Duration</div>
              </div>
              {(data.timeline || []).length === 0 ? (
                <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                  No activity events for this day.
                </div>
              ) : data.timeline.map((row, i) => (
                <div key={i} className="grid grid-cols-4 text-xs px-3 py-2 border-t items-center">
                  <div className="text-slate-800">{fmtTime(row.start)}</div>
                  <div className="text-slate-800">{fmtTime(row.end)}</div>
                  <div>
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${
                      row.activity === "IDLE"    ? "bg-red-100 text-red-700" :
                      row.activity === "BREAK"   ? "bg-sky-100 text-sky-700" :
                      row.activity === "DIALING" ? "bg-emerald-100 text-emerald-700" :
                                                   "bg-slate-100 text-slate-700"
                    }`}>{row.activity}</span>
                  </div>
                  <div className="text-right font-semibold">{row.duration_label}</div>
                </div>
              ))}
            </div>

            {/* Totals block */}
            <div className="grid grid-cols-2 gap-2">
              <TotalCell label="Logged-In Time"        value={data.totals_labels.logged_in} />
              <TotalCell label="Connected Talk Time"   value={data.totals_labels.connected_talk} />
              <TotalCell label="Idle Time"             value={data.totals_labels.idle}   tone="red" />
              <TotalCell label="Break Time"            value={data.totals_labels.break}  tone="sky" />
              <TotalCell label="Calls Attempted"       value={data.totals.calls_attempted}  raw />
              <TotalCell label="Calls Connected"       value={data.totals.calls_connected}  raw />
            </div>

            {/* PAYROLL — the number */}
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-4"
                 data-testid="payroll-calling-time-card">
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-widest text-emerald-700">
                <DollarSign size={12} /> Payroll Calling Time
              </div>
              <div className="mt-1 text-3xl font-bold text-emerald-900">
                {data.totals_labels.payroll_calling}
              </div>
              <div className="text-[11px] text-emerald-700/70 mt-1">
                Total dialer/calling time excluding idle &amp; break periods.
              </div>
            </div>
          </div>
        )}

        {/* WEEK VIEW — per-day breakdown */}
        {isWeek && !busy && (
          <div className="space-y-4">
            <div className="rounded-lg border overflow-hidden">
              <div className="grid grid-cols-5 text-[10px] uppercase font-semibold text-slate-500 bg-slate-50 px-3 py-2">
                <div>Date</div><div>Calling</div><div>Talk</div><div>Idle</div><div className="text-right">Calls</div>
              </div>
              {(data.days || []).map((d, i) => (
                <div key={i} className="grid grid-cols-5 text-xs px-3 py-2 border-t items-center">
                  <div>
                    <div className="font-semibold">{d.weekday}</div>
                    <div className="text-[10px] text-muted-foreground">{d.date}</div>
                  </div>
                  <div className="font-semibold text-emerald-700">{d.labels.calling}</div>
                  <div className="text-slate-700">{d.labels.talk}</div>
                  <div className="text-red-700">{d.labels.idle}</div>
                  <div className="text-right font-semibold">{d.calls_attempted}</div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50/50 p-4"
                 data-testid="payroll-calling-time-card">
              <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-widest text-emerald-700">
                <DollarSign size={12} /> TOTAL PAYROLL CALLING TIME
              </div>
              <div className="mt-1 text-3xl font-bold text-emerald-900">
                {data.totals_labels.payroll_calling}
              </div>
              <div className="text-[11px] text-emerald-700/70 mt-1">
                {(data.days || []).length} days · calculated from stored activity events.
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TotalCell({ label, value, raw, tone }) {
  const toneClass = tone === "red"  ? "text-red-700"
                  : tone === "sky"  ? "text-sky-700"
                  : "text-slate-800";
  return (
    <div className="rounded-lg border bg-white px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className={`text-lg font-bold ${toneClass}`}>{raw ? value : value || "0s"}</div>
    </div>
  );
}
